import { useEffect, useRef } from 'react'
import type {
	UseFormTrigger,
	Control,
	FieldValues,
	Path,
	PathValue,
} from 'react-hook-form'
import { useWatch } from 'react-hook-form'

/**
 * A React Hook for React Hook Form that automatically re-triggers validation
 * on specified fields when any of their dependent fields change. Note that this validation
 * is triggered on every change event.
 *
 * This is useful for creating interdependent form validations, such as:
 * - Validating that `startDate` is before `endDate`
 * - Re-checking a summary or total field when its contributing values change
 *
 * Internally, this hook watches the values of all dependent fields,
 * compares them to their previous values, and triggers re-validation
 * of only the fields that depend on those changed values.
 *
 *
 * @template TFieldValues - The type of your form values (typically inferred from useForm).
 *
 * @param {Control<TFieldValues>} control - The control object from `useForm()`.
 * @param {UseFormTrigger<TFieldValues>} trigger - The trigger function from `useForm()` used to manually trigger validation.
 * @param {{
 *   field: Path<TFieldValues>;
 *   dependsOn: Array<Path<TFieldValues>>;
 * }[]} dependencies - An array of objects where:
 *   - `field` is the name of the field to revalidate
 *   - `dependsOn` is an array of field names that should trigger revalidation of `field` when they change
 *
 * @note
 * NOTE: You still need to define validation rules for the inputs separately.
 *
 * @example
 * useRhfDependentValidation(control, trigger, [
 *   { field: 'startDate', dependsOn: ['endDate'] },
 *   { field: 'sum', dependsOn: ['amount', 'interestRate'] },
 * ]);
 *
 * // When 'endDate' changes, 'startDate' is revalidated.
 * // When 'amount' or 'interestRate' change, 'sum' is revalidated.
 */
export function useRhfDependentValidation<TFieldValues extends FieldValues>(
	control: Control<TFieldValues>,
	trigger: UseFormTrigger<TFieldValues>,
	dependencies: {
		field: Path<TFieldValues>
		dependsOn: Array<Path<TFieldValues>>
	}[],
) {
	const allDependencyFields = Array.from(
		new Set(dependencies.flatMap((dep) => dep.dependsOn)),
	)

	const watchedValues = useWatch({
		control,
		name: allDependencyFields,
	})

	const previousValuesRef = useRef<
		PathValue<TFieldValues, Path<TFieldValues>>[] | undefined
	>(undefined)

	useEffect(() => {
		const previousValues = previousValuesRef.current
		previousValuesRef.current = watchedValues

		if (!previousValues) return

		const changedDependencyFields = allDependencyFields.filter((_, index) => {
			const dependencyFieldValue = watchedValues[index]
			return dependencyFieldValue !== previousValues[index]
		})

		const changedSet = new Set(changedDependencyFields)

		dependencies.forEach(({ field, dependsOn }) => {
			const shouldTrigger = dependsOn.some((dep) => changedSet.has(dep))
			if (shouldTrigger) {
				trigger(field)
			}
		})
	}, [watchedValues, dependencies, trigger, allDependencyFields])
}
